import Vue from 'vue'
import store from 'store'
import { fetchAllMarketStockBriefs } from 'apis/stock'
import { MARKET_TYPE } from 'const/stock'
import getLocalFixedQuoteDetail from './getLocalFixedQuoteDetail'

let previousRequest = null

const isCanceled = err => err && err.aborted === true

export default class Stock {
  constructor(symbol) {
    this.symbol = symbol
  }

  static getStock(market, symbol) {
    if (market === MARKET_TYPE.US) return new USStock(symbol)
    if (market === MARKET_TYPE.HK) return new HKStock(symbol)
    if (market === MARKET_TYPE.SZ || market === MARKET_TYPE.SH) {
      return new AStock(symbol)
    }
    if (market === MARKET_TYPE.FUT || market === MARKET_TYPE.UK) return
    throw new Error(
      'invalid market, current only support US, HK, SZ, UK and SH'
    )
  }

  // 获取全市场股票详情
  static async getAllStockDetails(data, options) {
    const { data: respData } = await Vue.http.post(
      '~stock/stock_info/detail/global',
      data,
      options
    )

    return getLocalFixedQuoteDetail(respData.items || [])
  }

  // 获取全市场股票摘要
  static async getAllStockBriefs(data) {
    const { data: respData } = await fetchAllMarketStockBriefs(data)

    return getLocalFixedQuoteDetail(respData.items || [])
  }
}

class USStock extends Stock {
  getHourTradingTimeTrendData(beginTime = -1, tradingStatus = 0, options) {
    return Vue.http.get(
      '~stock/stock_info/time_trend/hour_trading_detail/{symbol}',
      {
        params: { beginTime, tradingStatus, symbol: this.symbol },
        ...options
      }
    )
  }

  getTimeTrendData(period = 'day', beginTime = -1, options) {
    return Vue.http.get(
      `~stock/stock_info/time_trend/${period}/${this.symbol}`,
      {
        params: { beginTime },
        ...options
      }
    )
  }

  getCandleData(
    period = 'day',
    beginTime = -1,
    endTime = -1,
    right = 'br',
    limit = 251,
    options
  ) {
    return Vue.http.get(
      `~stock/stock_info/candle_stick/${period}/${this.symbol}`,
      { params: { beginTime, endTime, right, limit }, ...options }
    )
  }

  getDetail(keep = false) {
    return Vue.http
      .get(`~stock/stock_info/detail/${this.symbol}`, {
        before: request => {
          if (keep) return
          if (previousRequest) {
            previousRequest.abort()
          }
          previousRequest = request
        }
      })
      .then(({ data: respData }) => {
        if (respData.ret === 0) {
          const detail = {
            serverTime: respData.serverTime,
            ...respData.items[0]
          }
          return getLocalFixedQuoteDetail(detail)
        }
        throw respData
      })
      .catch(err => {
        if (!isCanceled(err)) throw err
      })
      .finally(() => {
        previousRequest = null
      })
  }

  getFundsFlowTrend(beginTime = -1, symbol) {
    const url = `~stock/stock_info/fund_flow_trend/${symbol}`
    return Vue.http
      .get(url, { params: { beginTime, symbol } })
      .then(response => response.data)
  }
}

class HKStock extends Stock {
  getTimeTrendData(period = 'day', beginTime = -1, options) {
    return Vue.http.get(
      `~stock/hkstock/stock_info/time_trend/${period}/${this.symbol}`,
      {
        params: {
          beginTime,
          manualRefresh: true
        },
        ...options
      }
    )
  }

  getCandleData(
    period = 'day',
    beginTime = -1,
    endTime = -1,
    right,
    limit = 251
  ) {
    const params = { beginTime, endTime, limit, manualRefresh: true }
    if (right) {
      params.right = right
    }
    return Vue.http.get(
      `~stock/hkstock/stock_info/candle_stick/${period}/${this.symbol}`,
      { params }
    )
  }

  getDetail() {
    return Vue.http
      .get(`~stock/hkstock/stock_info/detail/${this.symbol}`, {
        before(request) {
          if (previousRequest) {
            previousRequest.abort()
          }
          previousRequest = request
        }
      })
      .then(({ data: respData }) => {
        if (respData.ret === 0) {
          const detail = {
            serverTime: respData.serverTime,
            ...respData.items[0]
          }
          return getLocalFixedQuoteDetail(detail)
        }
        throw respData
      })
      .catch(err => {
        if (!isCanceled(err)) throw err
      })
      .finally(() => {
        previousRequest = null
      })
  }

  getFundsFlowTrend(beginTime = -1, symbol) {
    const url = `~stock/hkstock/stock_info/fund_flow_trend/${symbol}`
    return Vue.http
      .get(url, { params: { beginTime, symbol } })
      .then(response => response.data)
  }
}

class AStock extends Stock {
  constructor(symbol) {
    super(symbol)
    this.dayCandleUrl = '~stock/astock/stock_info/candle_stick'
  }

  getTimeTrendData(period = 'day', beginTime = -1, options) {
    return Vue.http.get(`~stock/astock/chart/realtime/${this.symbol}`, {
      params: {
        beginTime,
        period
      },
      ...options
    })
  }

  getCandleData(
    period = 'day',
    beginTime = -1,
    endTime = -1,
    right = 'br',
    limit = 251
  ) {
    return Vue.http.get(`${this.dayCandleUrl}/${period}/${this.symbol}`, {
      params: { beginTime, endTime, right, limit, period }
    })
  }

  getDetail() {
    const detailPromise = Vue.http
      .get(`~stock/astock/symbol/meta/${this.symbol}`, {
        before(request) {
          const viewType = store.state.views.type
          if (viewType === 1 && previousRequest) {
            previousRequest.abort()
          }
          previousRequest = request
        }
      })
      .then(response => {
        if (
          !response.data ||
          !response.data.items ||
          !response.data.items.length
        ) {
          return null
        }
        const detail = getLocalFixedQuoteDetail(response.data.items[0])
        Object.assign(detail, {
          tradable: !detail.isIndex,
          change: parseFloat(detail.changeValue),
          changeRate: parseFloat(
            detail.changePercent.substr(0, detail.changePercent.length - 1) /
              100
          ),
          nameCN: detail.name,
          latestPrice: parseFloat(detail.lastPrice),
          exchange: detail.market,
          high: parseFloat(detail.dayHigh),
          low: parseFloat(detail.dayLow),
          open: parseFloat(detail.todayOpen),
          preClose: parseFloat(detail.preClose),
          timestamp: detail.serverTime,
          secType: 'STK'
        })
        return detail
      })
      .catch(err => {
        if (!isCanceled(err)) throw err
      })
      .finally(() => {
        previousRequest = null
      })
    // 资金成交分布接口
    const fundPromise = Vue.http
      .get(`~stock/astock/symbol/publicity_fund/${this.symbol}`)
      .then(response => {
        return response.data.fund
      })
    return Promise.all([detailPromise, fundPromise]).then(response => {
      if (response[0] && response[1]) {
        return Object.assign(response[0], response[1])
      }
      return response[0] || response[1] || {}
    })
  }
}
